// CORE
import { useAppSelector } from 'hooks/redux';
import React from 'react';

// App-Components
import { accountsUtils } from 'app-components/flux/accounts';
import type { scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

// MUI
import DownloadingIcon from '@mui/icons-material/DownloadingRounded';
import ErrorIcon from '@mui/icons-material/ErrorOutlineRounded';
import HourglassIcon from '@mui/icons-material/HourglassBottomRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import UpdatedIcon from '@mui/icons-material/VerifiedRounded';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{ size: number; error: boolean; isPending: boolean; open: boolean; updated: boolean }>()(
  (theme, { size, error, isPending, open, updated }) => ({
    icon: {
      height: size,
      width: size,
      bottom: -size / 4,
      right: -size / 2 + size / 8,
      position: 'absolute',
      borderRadius: size / 2,

      zIndex: 1,
      backgroundColor: open && !error ? theme.palette.pending.main : theme.palette.background.paper,
      color: error
        ? theme.palette.error.main
        : updated
          ? theme.palette.success.main
          : open
            ? theme.palette.common.white
            : theme.palette.primary.main,
      padding: error ? (size - 16) / 8 : isPending ? size / 6 : size / 16,
      boxShadow: theme.shadows[13],
    },
  }),
);

interface ConnectedIconProps {
  model?: scheduledTransactionsTypes.ScheduledTransactionType;
  txn?: TransactionProps;
  size?: number;
  hasBVA?: boolean;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function ConnectedIcon(props: ConnectedIconProps) {
  const { model, size = 24, txn, hasBVA } = props;

  let showIcon = false;
  let updated = false;
  let isPending = false;
  let open = false;
  let error = false;
  let title = '';
  let Icon: any = LinkIcon;

  const displayTxn = txn || model?.transaction || ({} as TransactionProps);
  const creditAccount = useAppSelector((state) => transactionsSelectors.getCreditAccountFromTxn(state, displayTxn));

  if (hasBVA) {
    // BVA stuff
    showIcon = true;
    title = 'Connected';
    // eslint-disable-next-line
    if (accountsUtils.isAccountInError(creditAccount)?.isError) {
      title = 'Connection Error';
      Icon = ErrorIcon;
      error = true;
    } else if (txn?.ebillId) {
      if (
        txn.ebillId === '1' || // Bank/BVA statement amount
        txn.ebillId === '2' // QCS sum after statement close
      ) {
        title = 'Your bill amount has been finalized.';
        Icon = UpdatedIcon;
        updated = true;
      } else if (txn.ebillId === '3') {
        // QCS sum while statement open
        title = 'Statement period has begun and amount will continue to update.';
        open = true;
        Icon = DownloadingIcon;
      }
    }
  } else if (model?.presentmentAccountId) {
    // fiServ presentment
    showIcon = true;
    title = 'Connected. Bill will be available soon';
    switch (model.billerEnrollmentStatus) {
      case 'PENDING':
        isPending = true;
        title = 'Connecting. This may take a day or two';
        Icon = HourglassIcon;
        break;
      case 'DISCONNECTED':
      case 'FAILED':
        title = 'Biller requires attention';
        Icon = ErrorIcon;
        error = true;
        break;
      case 'CONNECTED':
        if (txn?.ebillId) {
          title = 'Bill details are up-to-date';
          Icon = UpdatedIcon;
          updated = true;
        }
        break;
    }
  }

  const { classes } = useStyles({ size, error, isPending, open, updated });

  return showIcon ? (
    <Tooltip title={title} arrow enterDelay={300}>
      <Icon className={classes.icon} size={size} />
    </Tooltip>
  ) : null;
}

ConnectedIcon.displayName = 'ConnectedIcon';

export default ConnectedIcon;
